import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { formatUTCToLocal } from '@/utils/date'
import { Power, PowerOff, Send, RefreshCw, Loader2 } from 'lucide-react'
import {
  orbcommService,
  PREDEFINED_MESSAGES,
  getMessageStateLabel,
} from '@/services/orbcomm.service'
import type { OrbcommMessage } from '@/services/orbcomm.service'
import type { Device } from '@/types/device.types'

interface OrbcommPanelProps {
  device: Device
}

export const OrbcommPanel = ({ device }: OrbcommPanelProps) => {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Terminal status
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery({
    queryKey: ['orbcomm-status', device.id],
    queryFn: () => orbcommService.getTerminalStatus(device.id),
    staleTime: 30000,
  })

  // Message history (manual fetch)
  const [messages, setMessages] = useState<OrbcommMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesLoaded, setMessagesLoaded] = useState(false)

  const loadMessages = async () => {
    setMessagesLoading(true)
    try {
      const data = await orbcommService.getMessageHistory(device.id)
      setMessages(data)
      setMessagesLoaded(true)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load message history',
        variant: 'destructive',
      })
    } finally {
      setMessagesLoading(false)
    }
  }

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: () => orbcommService.activateTerminal(device.id),
    onSuccess: (data) => {
      toast({ title: 'Success', description: data.message || 'Terminal activated' })
      queryClient.invalidateQueries({ queryKey: ['orbcomm-status', device.id] })
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to activate terminal', variant: 'destructive' })
    },
  })

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: () => orbcommService.deactivateTerminal(device.id),
    onSuccess: (data) => {
      toast({ title: 'Success', description: data.message || 'Terminal deactivated' })
      queryClient.invalidateQueries({ queryKey: ['orbcomm-status', device.id] })
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to deactivate terminal', variant: 'destructive' })
    },
  })

  // Send message
  const [selectedMessage, setSelectedMessage] = useState('')
  const [userMessageId, setUserMessageId] = useState('')

  const sendMessageMutation = useMutation({
    mutationFn: () => {
      const umid = userMessageId.trim() ? parseInt(userMessageId, 10) : undefined
      return orbcommService.sendMessage(device.id, selectedMessage, umid)
    },
    onSuccess: (data) => {
      toast({ title: 'Success', description: data.message || 'Message sent' })
      setSelectedMessage('')
      setUserMessageId('')
      // Refresh message history if already loaded
      if (messagesLoaded) loadMessages()
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' })
    },
  })

  const canActivate = status?.state === 'D'
  const canDeactivate = status?.state === 'A' || status?.state === 'P' || status?.state === 'E'

  const getStateBadge = (state: string) => {
    switch (state) {
      case 'A':
        return <Badge className="bg-green-500">Active</Badge>
      case 'D':
        return <Badge variant="secondary">Deactivated</Badge>
      case 'P':
        return <Badge className="bg-yellow-500">Pending</Badge>
      case 'E':
        return <Badge className="bg-blue-500">Enabled</Badge>
      default:
        return <Badge variant="outline">{state}</Badge>
    }
  }

  return (
    <div className="space-y-4">
      {/* Terminal Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Terminal Status</CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <LoadingSpinner />
          ) : statusError ? (
            <p className="text-sm text-destructive">Failed to load terminal status</p>
          ) : status ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Modem ID</p>
                  <p className="text-sm">{status.modemId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">State</p>
                  <div className="mt-0.5">{getStateBadge(status.state)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Enabled</p>
                  <p className="text-sm">{status.enabled ? 'Yes' : 'No'}</p>
                </div>
                {status.softwareVersion && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Software Version</p>
                    <p className="text-sm">{status.softwareVersion}</p>
                  </div>
                )}
                {status.hardwareVersion && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Hardware Version</p>
                    <p className="text-sm">{status.hardwareVersion}</p>
                  </div>
                )}
                {status.partNumber && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Part Number</p>
                    <p className="text-sm">{status.partNumber}</p>
                  </div>
                )}
                {status.gatewayAccountId && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Gateway Account</p>
                    <p className="text-sm">{status.gatewayAccountId}</p>
                  </div>
                )}
              </div>

              {status.statusMessage && (
                <p className="text-sm text-muted-foreground">{status.statusMessage}</p>
              )}

              <div className="flex items-center gap-3 pt-2">
                {canActivate && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="default" className="bg-green-600 hover:bg-green-700">
                        <Power className="mr-2 h-4 w-4" />
                        Activate
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Activate terminal?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will activate the Orbcomm terminal for <strong>{device.name}</strong>.
                          Fees apply for activation.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => activateMutation.mutate()}
                          disabled={activateMutation.isPending}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {activateMutation.isPending ? 'Activating...' : 'Activate'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {canDeactivate && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive">
                        <PowerOff className="mr-2 h-4 w-4" />
                        Deactivate
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Deactivate terminal?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will deactivate the Orbcomm terminal for <strong>{device.name}</strong>.
                          <span className="block mt-2 font-medium text-destructive">
                            Warning: Fees apply for deactivation and re-activation.
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deactivateMutation.mutate()}
                          disabled={deactivateMutation.isPending}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Send Message */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Send Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedMessage} onValueChange={setSelectedMessage}>
              <SelectTrigger className="sm:w-[320px]">
                <SelectValue placeholder="Select a message..." />
              </SelectTrigger>
              <SelectContent>
                {PREDEFINED_MESSAGES.map((msg) => (
                  <SelectItem key={msg.value} value={msg.value}>
                    {msg.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              placeholder="User Message ID (optional)"
              className="sm:w-[200px]"
              value={userMessageId}
              onChange={(e) => setUserMessageId(e.target.value)}
            />
            <Button
              onClick={() => sendMessageMutation.mutate()}
              disabled={!selectedMessage || sendMessageMutation.isPending}
            >
              {sendMessageMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Message History */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Message History</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={loadMessages}
            disabled={messagesLoading}
          >
            {messagesLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {messagesLoaded ? 'Refresh' : 'Load History'}
          </Button>
        </CardHeader>
        <CardContent>
          {!messagesLoaded ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Click "Load History" to view message history
            </p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No messages found
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fwd Msg ID</TableHead>
                    <TableHead>State Time</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Is Closed</TableHead>
                    <TableHead>Transport</TableHead>
                    <TableHead>Ref Number</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map((msg, idx) => (
                    <TableRow key={msg.forwardMessageId || idx}>
                      <TableCell className="font-mono text-xs">
                        {msg.forwardMessageId}
                      </TableCell>
                      <TableCell className="text-sm">
                        {msg.stateUtc ? formatUTCToLocal(msg.stateUtc) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={msg.state === 8 ? 'default' : msg.state === 2 || msg.state === 3 ? 'destructive' : 'secondary'}
                        >
                          {getMessageStateLabel(msg.state)}
                        </Badge>
                        {msg.errorId != null && msg.errorId !== 0 && (
                          <span className="ml-2 text-xs text-destructive">
                            Error: {msg.errorId}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {msg.isClosed ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {msg.transport || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {msg.referenceNumber || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
